/**
 * Guided fields, reference options and inline Department creation: UI types. They mirror docs/job-role-guided-fields-contract.md
 * (sections 2.1 to 2.5), whose wire shapes the backend implements. wireMapping.ts is the only seam between the two.
 *
 * Nothing here touches Identity, a membership or a permission: a job role (and every option, occupation and department on this
 * page) describes what someone does, never what they can do in the product.
 */

// ---- capabilities (contract 2.5): booleans only, a UI hint; the server still enforces every action ----

export interface JobRoleCapabilities {
  canCreateRole: boolean;
  canUpdateRole: boolean;
  canCreateDepartment: boolean;
  canCreateReferenceOption: boolean;
  canViewBenchmarks: boolean;
  canManagePayBand: boolean;
}

// ---- occupation reference data (SOC and O*NET, contract 2.1) ----

export type OccupationSystem = 'Soc' | 'Onet';

/** Where an occupation list came from. Always shown beside a lookup: the official data is attributed and versioned. */
export interface OccupationDataset {
  name: string;
  version: string;
  sourceOrganization: string;
  sourceUrl: string | null;
  licenseName: string | null;
  licenseUrl: string | null;
  attributionText: string | null;
  limitation: string | null;
  retrievedOn: string | null;
  /** Only on the dataset list (`GET /api/reference/occupation-datasets`). */
  isCurrent?: boolean | undefined;
  system?: OccupationSystem | undefined;
}

export type OccupationMatchKind = 'Code' | 'Title' | 'Synonym';

export interface OccupationItem {
  code: string;
  title: string;
  description: string | null;
  majorGroupCode: string | null;
  majorGroupName: string | null;
  /** For an O*NET row, its 6-digit SOC parent; for a SOC row, itself. */
  socCode: string | null;
  matchedOn: OccupationMatchKind | null;
  /** The reported title that matched when `matchedOn` is `Synonym`. */
  matchedText: string | null;
}

export interface OccupationSearchResult {
  system: OccupationSystem;
  /** Null when no dataset has been imported in this environment. */
  dataset: OccupationDataset | null;
  items: OccupationItem[];
  totalMatches: number;
}

export interface OccupationSearchParams {
  system: OccupationSystem;
  search?: string | undefined;
  /** For `system=Onet`: only O*NET codes under this SOC code. */
  socCode?: string | undefined;
  take?: number | undefined;
}

// ---- reference options (contract 2.2): EPN standard options plus the company's own custom values ----

export const REFERENCE_CATEGORIES = [
  'JobFamily', 'CareerLevel', 'ManagementLevel', 'EducationLevel', 'ExperienceRange', 'WorkArrangement', 'TravelRequirement', 'Skill',
  'Certification', 'License', 'Language', 'PhysicalRequirement', 'WorkingCondition', 'FlsaClassification', 'BenefitEligibility',
  'RecommendedDepartment', 'PerformanceIndicator',
] as const;
export type ReferenceCategory = (typeof REFERENCE_CATEGORIES)[number];

/** Custom values are refused by the server for these two controlled lists (contract 2.2). */
export const CONTROLLED_CATEGORIES: readonly ReferenceCategory[] = ['CareerLevel', 'FlsaClassification'];

export interface ReferenceOption {
  /** Null for a company custom option. */
  code: string | null;
  name: string;
  description: string | null;
  /** The functional area a standard option is filed under; null for a custom option. */
  group: string | null;
  custom: boolean;
  synonyms: string[];
}

export type ReferenceGroupKey = 'Recommended' | 'Company' | 'Standard';

export interface ReferenceOptionGroup {
  key: ReferenceGroupKey | string;
  label: string;
  options: ReferenceOption[];
}

export interface ReferenceOptionList {
  category: ReferenceCategory;
  datasetVersion: string | null;
  groups: ReferenceOptionGroup[];
  totalStandard: number;
  /** True when more standard options exist than were returned: the screen says so and asks for a narrower search. */
  truncated: boolean;
}

export interface ListReferenceOptionsParams {
  category: ReferenceCategory;
  search?: string | undefined;
  take?: number | undefined;
  /** Standard options this EPN template references come first, in a "Recommended for this role" group. */
  templateId?: string | undefined;
}

export interface CreateReferenceOptionResult {
  option: ReferenceOption;
  /** False when the value already existed (a company custom one, or a standard name or synonym): the existing one is returned. */
  created: boolean;
}

// ---- role code suggestion (contract 2.4) ----

export interface RoleCodeSuggestionParams {
  title?: string | undefined;
  departmentId?: string | null | undefined;
  jobFamily?: string | undefined;
  careerLevel?: string | undefined;
  /** What the person typed: its availability is checked. */
  proposed?: string | undefined;
  /** The role being edited, so its own code is never reported as taken. */
  excludeRoleId?: string | undefined;
}

export interface RoleCodeSuggestion {
  suggestion: string;
  /** Null when no code was proposed. */
  proposedAvailable: boolean | null;
}

// ---- classification (SOC and O*NET codes the person confirmed, or "not yet classified" with a reason) ----

export type ClassificationStatus = 'Confirmed' | 'NotYetClassified';

export interface JobRoleClassification {
  socStatus: ClassificationStatus | null;
  socNotYetClassifiedReason: string | null;
  /** Stamped by the server from the dataset current at save time; a value sent by the client is ignored. */
  socDatasetVersion: string | null;
  onetStatus: ClassificationStatus | null;
  onetNotYetClassifiedReason: string | null;
  onetDatasetVersion: string | null;
}

export function emptyClassification(): JobRoleClassification {
  return {
    socStatus: null, socNotYetClassifiedReason: null, socDatasetVersion: null,
    onetStatus: null, onetNotYetClassifiedReason: null, onetDatasetVersion: null,
  };
}

// ---- problem codes added by the guided fields work (contract 2.1 to 2.3). Never shown to users. ----

export const GuidedErrorCode = {
  DepartmentNameExists: 'DEPARTMENT_NAME_EXISTS',
  DepartmentCodeExists: 'DEPARTMENT_CODE_EXISTS',
  DepartmentCodeInvalid: 'DEPARTMENT_CODE_INVALID',
  ReferenceOptionInvalid: 'REFERENCE_OPTION_INVALID',
  OccupationSystemInvalid: 'OCCUPATION_SYSTEM_INVALID',
  OccupationNotFound: 'OCCUPATION_NOT_FOUND',
  OccupationCodeUnknown: 'OCCUPATION_CODE_UNKNOWN',
  OccupationCodeInvalid: 'OCCUPATION_CODE_INVALID',
  ClassificationInvalid: 'CLASSIFICATION_INVALID',
} as const;
